import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../contexts/ApiContext";
import { useFeatures } from "../../contexts/FeatureContext";
import RecordModal from "./RecordModal";
import "../../styles/crud.css";
import "./PlayersPage.css";

const EMPTY_FORM = { name: "" };

function WinPctBar({ wins, losses }) {
  const total = wins + losses;
  if (total === 0) return <span className="crud-none">—</span>;
  const pct = Math.round((wins / total) * 100);
  return (
    <div className="win-pct-wrap">
      <div className="win-pct-bar">
        <div className="win-pct-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="win-pct-label">{pct}%</span>
    </div>
  );
}

function EloDisplay({ elo }) {
  if (elo == null) return <span className="elo-unranked">Unranked</span>;
  return <span className="elo-value">{elo}</span>;
}

function PlayersPage() {
  const { playersApi } = useApi();
  const { flags } = useFeatures();
  const ratingsOn = flags.ratingsEnabled;
  const queryClient = useQueryClient();

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [recordPlayer, setRecordPlayer] = useState(null);

  const {
    data: players = [],
    isLoading: loading,
    isError: loadError,
  } = useQuery({
    queryKey: ["players"],
    queryFn: () => playersApi.getAll(),
  });

  const invalidatePlayers = () =>
    queryClient.invalidateQueries({ queryKey: ["players"] });

  const saveMutation = useMutation({
    mutationFn: ({ id, data }) =>
      id ? playersApi.update(id, data) : playersApi.create(data),
    onSuccess: () => {
      setForm(EMPTY_FORM);
      setEditing(null);
      setError("");
      invalidatePlayers();
    },
    onError: (e) => setError(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => playersApi.delete(id),
    onSuccess: invalidatePlayers,
    onError: (e) => setError(e.message),
  });

  function handleSubmit() {
    if (!form.name.trim()) return setError("Name is required.");
    saveMutation.mutate({ id: editingId, data: form });
  }

  function handleDelete(id) {
    deleteMutation.mutate(id);
  }

  function handleEdit(player) {
    setEditing(player.id);
    setForm({ name: player.name });
    setError("");
  }

  function handleCancel() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError("");
  }

  const displayError =
    error || (loadError ? "Could not load players. Is the API running?" : "");

  return (
    <div className="crud-page">
      {recordPlayer && (
        <RecordModal
          player={recordPlayer}
          onClose={() => setRecordPlayer(null)}
        />
      )}

      <div className="crud-header">
        <h1 className="crud-title">Players</h1>
        <p className="crud-sub">
          {players.length} player{players.length !== 1 ? "s" : ""} registered
        </p>
      </div>

      <div className="crud-form">
        <h2 className="crud-form-title">
          {editingId ? "Edit Player" : "Add Player"}
        </h2>
        <div className="crud-form-row">
          <input
            className="tn-input"
            placeholder="Player name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          />
          <button className="crud-btn-primary" onClick={handleSubmit}>
            {editingId ? "Save" : "Add"}
          </button>
          {editingId && (
            <button className="crud-btn-ghost" onClick={handleCancel}>
              Cancel
            </button>
          )}
        </div>
        {displayError && <p className="tn-err">{displayError}</p>}
      </div>

      {loading ? (
        <p className="crud-loading">Loading...</p>
      ) : players.length === 0 ? (
        <p className="crud-empty">No players yet. Add one above.</p>
      ) : (
        <div className="crud-table-wrap">
          <table className="crud-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                {ratingsOn && <th>Rating</th>}
                <th>W</th>
                <th>L</th>
                <th>Win %</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {players.map((p, i) => (
                <tr key={p.id}>
                  <td className="rank-cell">
                    {!ratingsOn ? i + 1 : p.elo != null ? i + 1 : "—"}
                  </td>
                  <td>{p.name}</td>
                  {ratingsOn && (
                    <td>
                      <EloDisplay elo={p.elo} />
                    </td>
                  )}
                  <td className="stat-win">{p.wins ?? 0}</td>
                  <td className="stat-loss">{p.losses ?? 0}</td>
                  <td>
                    <WinPctBar wins={p.wins ?? 0} losses={p.losses ?? 0} />
                  </td>
                  <td className="crud-actions">
                    <button
                      className="crud-btn-ghost"
                      onClick={() => setRecordPlayer(p)}
                    >
                      View Record
                    </button>
                    <button
                      className="crud-btn-ghost"
                      onClick={() => handleEdit(p)}
                    >
                      Edit
                    </button>
                    <button
                      className="crud-btn-danger"
                      onClick={() => handleDelete(p.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PlayersPage;
